import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { firestore, collection, query, where, getDocs } from '../lib/firebase';
import { User, Trophy, Calendar, ArrowLeft } from 'lucide-react';
import { getAvatarById } from './AvatarPicker';

interface ProfileData {
  id: string;
  username: string;
  avatar_url?: string;
  created_at: string;
}

interface Stats {
  total_games: number;
  wins: number;
  draws: number;
  losses: number;
}

export default function PublicProfile() {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [stats, setStats] = useState<Stats>({ total_games: 0, wins: 0, draws: 0, losses: 0 });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (username) fetchProfile(username);
  }, [username]);

  const fetchProfile = async (name: string) => {
    setLoading(true);
    setNotFound(false);

    try {
      // Find profile by username
      const q = query(collection(firestore, 'profiles'), where('username', '==', name));
      const snap = await getDocs(q);

      if (snap.empty) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const profileDoc = snap.docs[0];
      const data = { id: profileDoc.id, ...profileDoc.data() } as ProfileData;
      setProfile(data);

      // Fetch stats from gameResults
      const resultsSnap = await getDocs(collection(firestore, 'gameResults'));
      const results = resultsSnap.docs
        .map(d => d.data())
        .filter(r => r.player1_id === data.id || r.player2_id === data.id);

      if (results.length > 0) {
        setStats({
          total_games: results.length,
          wins: results.filter(g => g.winner_id === data.id).length,
          draws: results.filter(g => g.is_draw).length,
          losses: results.filter(g => g.winner_id && g.winner_id !== data.id).length,
        });
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4 text-center">
        <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h2 className="text-2xl font-bold mb-2">Player Not Found</h2>
        <p className="text-gray-300 mb-6">No player with username "{username}" exists.</p>
        <Link to="/" className="text-pink-400 hover:text-pink-300 transition flex items-center justify-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </div>
    );
  }

  const avatar = getAvatarById(profile.avatar_url);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <Link to="/leaderboard" className="text-gray-300 hover:text-white transition flex items-center gap-2 mb-6 text-sm">
        <ArrowLeft className="w-4 h-4" /> Leaderboard
      </Link>

      <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 bg-gradient-to-br from-pink-600 to-violet-600 rounded-full flex items-center justify-center text-3xl shrink-0">
            {avatar ? avatar.emoji : <User className="w-10 h-10 text-white" />}
          </div>
          <div>
            <h1 className="text-2xl font-bold">{profile.username}</h1>
            <div className="flex items-center gap-1 text-gray-300 text-sm mt-1">
              <Calendar className="w-3.5 h-3.5" />
              Joined {new Date(profile.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-6 border border-white/10 mt-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Game Statistics
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-pink-500">{stats.total_games}</div>
            <div className="text-sm text-gray-300">Games</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-500">{stats.wins}</div>
            <div className="text-sm text-gray-300">Wins</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-500">{stats.draws}</div>
            <div className="text-sm text-gray-300">Draws</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-500">{stats.losses}</div>
            <div className="text-sm text-gray-300">Losses</div>
          </div>
        </div>
        {stats.total_games > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10 text-center">
            <span className="text-gray-300 text-sm">Win Rate: </span>
            <span className="text-lg font-semibold text-pink-500">
              {Math.round((stats.wins / stats.total_games) * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
